import { useState, useEffect } from 'react'
import { Button } from './button'
import { apiClient } from '@/lib/api'

interface SmsMessage {
  ts: number
  direction: string
  body: string
  event: string
  by: string
  entry_id: string
  is_admin_action?: boolean
  pk?: string
  sk?: string
  order_number?: number
  type?: string
  sms_failed?: boolean
  sms_failure_reason?: string
}

interface SmsHistoryModalProps {
  isOpen: boolean
  onClose: () => void
  phone: string
  tenantId: string
  locationId: string
  serviceId: string
  entryId?: string
}

export function SmsHistoryModal({ 
  isOpen, 
  onClose, 
  phone, 
  tenantId, 
  locationId, 
  serviceId, 
  entryId 
}: SmsHistoryModalProps) {
  const [messages, setMessages] = useState<SmsMessage[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [smsDisabled, setSmsDisabled] = useState(false)

  useEffect(() => {
    if (isOpen && phone && tenantId && locationId && serviceId) {
      checkSmsStatus()
      fetchSmsHistory()
    }
  }, [isOpen, phone, tenantId, locationId, serviceId, entryId])

  const checkSmsStatus = async () => {
    try {
      const settings = await apiClient.getTenantSettings(tenantId)
      setSmsDisabled(settings.disable_sms || false)
    } catch (err) {
      console.error('Failed to check SMS status:', err)
      setSmsDisabled(false)
    }
  }

  const fetchSmsHistory = async () => {
    console.log('fetchSmsHistory called with:', { tenantId, locationId, serviceId, phone, entryId })
    setLoading(true)
    setError(null)
    
    try {
      console.log('Making API call to getSmsHistory...')
      const response = await apiClient.getSmsHistory(tenantId, locationId, serviceId, {
        phone: phone,
        entryId: entryId
      })
      console.log('SMS History API response:', response)
      console.log('First message with pk/sk:', response.messages?.[0])
      setMessages(response.messages || [])
    } catch (err) {
      console.error('SMS History API error:', err)
      setError(err instanceof Error ? err.message : 'Failed to load SMS history')
    } finally {
      setLoading(false)
    }
  }

  const formatTime = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleString()
  }

  const copyAllHistory = () => {
    const historyText = messages.map(msg => {
      const time = formatTime(msg.ts)
      const sender = msg.direction === 'in' ? 'Customer' : 
                    msg.is_admin_action ? (msg.by?.startsWith('admin:') ? msg.by.replace('admin:', '') : 'Admin') : 'System'
      const orderNum = msg.order_number ? ` (#${msg.order_number})` : ''
      const event = msg.event ? ` (${msg.event})` : ''
      return `${sender}: ${time}${event}${orderNum}\n${msg.body}\n`
    }).join('\n---\n\n')
    
    navigator.clipboard.writeText(`Customer History — ${phone}\n\n${historyText}`)
      .then(() => alert('History copied to clipboard!'))
      .catch(() => alert('Failed to copy to clipboard'))
  }

  const downloadHistory = () => {
    const historyText = messages.map(msg => {
      const time = formatTime(msg.ts)
      const sender = msg.direction === 'in' ? 'Customer' : 
                    msg.is_admin_action ? (msg.by?.startsWith('admin:') ? msg.by.replace('admin:', '') : 'Admin') : 'System'
      const orderNum = msg.order_number ? ` (#${msg.order_number})` : ''
      const event = msg.event ? ` (${msg.event})` : ''
      return `${sender}: ${time}${event}${orderNum}\n${msg.body}`
    }).join('\n\n---\n\n')
    
    const blob = new Blob([`Customer History — ${phone}\n\n${historyText}`], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `sms-history-${phone}-${new Date().toISOString().split('T')[0]}.txt`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
        <div className="flex justify-between items-center p-6 border-b">
          <h3 className="text-lg font-semibold">Customer History — {phone}</h3>
          <div className="flex gap-2">
            {messages.length > 0 && (
              <>
                <Button variant="outline" size="sm" onClick={copyAllHistory}>
                  Copy All
                </Button>
                <Button variant="outline" size="sm" onClick={downloadHistory}>
                  Download
                </Button>
              </>
            )}
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
        
        <div className="p-6">
          {loading && (
            <div className="text-center py-8">Loading...</div>
          )}
          
          {error && (
            <div className="text-red-600 text-center py-8">{error}</div>
          )}
          
          {!loading && !error && messages.length === 0 && (
            <div className="text-center py-8">
              {smsDisabled ? (
                <div className="space-y-2">
                  <div className="text-orange-600 font-semibold">📵 SMS Disabled</div>
                  <div className="text-gray-600 text-sm">
                    SMS messaging is currently disabled for this tenant.
                    <br />
                    Enable SMS in Settings to start sending messages.
                  </div>
                </div>
              ) : (
                <div className="text-gray-500">
                  No messages found for this queue record.
                </div>
              )}
            </div>
          )}
          
          {!loading && !error && messages.length > 0 && (
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {messages.map((msg, index) => {
                const isAdminAction = msg.is_admin_action || msg.direction === 'out'
                const isCustomerAction = msg.direction === 'in'
                
                return (
                  <div
                    key={index}
                    className={`p-3 rounded-lg ${
                      isCustomerAction
                        ? 'bg-blue-50 ml-4 border-l-4 border-blue-300' 
                        : isAdminAction
                        ? 'bg-orange-50 mr-4 border-l-4 border-orange-300'
                        : 'bg-gray-50 mx-2 border-l-4 border-gray-300'
                    }`}
                  >
                    <div className="text-xs text-gray-600 mb-2">
                      <strong className={isAdminAction ? 'text-orange-700' : isCustomerAction ? 'text-blue-700' : 'text-gray-700'}>
                        {isCustomerAction ? 'Customer' : isAdminAction ? 
                          (msg.by && msg.by.startsWith('admin:') ? msg.by.replace('admin:', '') : 'Admin') 
                          : 'System'}:
                      </strong>{' '}
                      {formatTime(msg.ts)}
                      {msg.event && (
                        <span className="ml-2 text-gray-500">({msg.event})</span>
                      )}
                      {msg.order_number && <span className="ml-2 text-blue-600 font-bold">#{msg.order_number}</span>}
                      {msg.sms_failed && (
                        <span className="ml-2 text-red-600 font-bold" title={msg.sms_failure_reason}>
                          🚨 SMS FAILED
                        </span>
                      )}
                    </div>
                    <div className="text-sm">
                      {msg.body}
                      {msg.sms_failed && msg.sms_failure_reason && (
                        <div className="mt-1 text-xs text-red-600 italic">
                          Reason: {msg.sms_failure_reason}
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
          
          {!loading && !error && messages.length > 0 && (
            <div className="mt-4 text-xs text-gray-500 text-center">
              Total messages: {messages.length} | Limited to this queue record
            </div>
          )}
        </div>
      </div>
    </div>
  )
}